"use client";

import {
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type Ref,
  useRef,
  useState,
  type FC,
} from "react";
import { isEmpty, isString, mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";
import { format, parse } from "date-fns";

import { BaseInputField, type BaseInputFieldProps } from "../BaseInputField";
import { Icon } from "../Icon";
import { Popup, type PopupProps } from "../Popup";
import { Text } from "../Text";
import { mergeRefs } from "../../utils/mergeRefs";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useFormValidation } from "../../hooks/useFormValidation";
import { DatePicker } from "./components/DatePicker";
import type { ValidatableProps } from "../../props/ValidatableProps";

import { typography } from "../../theme/css/typography.css";
import { inputStyle } from "../BaseInputField/BaseInputField.css";
import { activeOptionStyle, iconStyle } from "./DateField.css";

export type DateFieldProps = BaseInputFieldProps &
  Pick<InputHTMLAttributes<HTMLInputElement>, "placeholder"> &
  ValidatableProps & {
    onChange?: DateFieldChangeHandler;
    onOutsideClick?: () => void;
    value?: string;
    defaultValue?: string;
    popup?: Pick<PopupProps, "placement" | "minWidth" | "maxWidth" | "width">;
    inputRef?: Ref<HTMLInputElement>;
    inputFormat?: string;
    outputFormat?: string;
    displayFormat?: string;
  };

export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";

export type DateFieldChangeHandler = (value: string) => void;

/**
 *  DateField component is a dropdown component that allows users to DateField a single option from a list of options.
 */
export const DateField: FC<DateFieldProps> = ({
  id: idProp,
  className,
  placeholder,
  inputRef,
  disabled: disabledProp,
  value,
  defaultValue,
  display,
  popup,
  variant = "default",
  autoFocus = false,
  inputFormat = DEFAULT_DATE_FORMAT,
  outputFormat = DEFAULT_DATE_FORMAT,
  error: errorProp,
  validation,
  name,
  onChange,
  onClick,
  onOutsideClick,
  ref,
  ...props
}) => {
  const { t } = useTranslation();

  const proxyRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const [isActive, setIsActive] = useState(autoFocus);

  const { id, disabled, error } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
    ref: proxyRef,
  });

  useClickOutside([popupRef, fieldRef], () => {
    closePopup();
    onOutsideClick?.();
  });

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    onClick?.(e);
    openPopup();
  };

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setIsActive(true);
  };

  const handleClose = () => {
    closePopup();
  };

  const closePopup = () => {
    if (isActive && proxyRef.current) {
      proxyRef.current.focus();
    }
    setIsActive(false);
  };

  const selectedDate = (() => {
    try {
      const date =
        isString(value) && value
          ? parse(value, inputFormat, new Date())
          : undefined;

      return date;
    } catch (error) {
      console.error("Failed to parse date:", error);
      return undefined;
    }
  })();

  const selectedLabel = (() => {
    if (!selectedDate) {
      return undefined;
    }

    try {
      const month = t(
        `dates.months.full.${format(selectedDate, "MMM").toLowerCase()}`,
      );
      return `${format(selectedDate, "d")} ${month.toLocaleLowerCase()} ${selectedDate.getFullYear()}`;
    } catch (error) {
      console.warn(`Failed to format date "${selectedDate}"`, error);
      return undefined;
    }
  })();

  const handleDatePickerChange = (date: Date) => {
    onChange?.(format(date, outputFormat));
  };

  const handleProxyKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      openPopup();
    } else if (e.key === "Tab") {
      return;
    }
    e?.preventDefault();
  };

  const handleProxyChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    console.warn("handleInputChange", e.target.value);
  };

  return (
    <Popup
      ref={popupRef}
      active={isActive}
      onClose={handleClose}
      applyActive
      escape
      content={
        <DatePicker value={selectedDate} onChange={handleDatePickerChange} />
      }
      variant={variant === "toggle" ? "toggle" : undefined}
      {...popup}
      placement={popup?.placement ?? "bottomRight"}
    >
      <BaseInputField
        // ref = is being overruled by popup
        altRef={mergeRefs(ref, fieldRef)}
        onClick={handleClick}
        disabled={disabled}
        labelFloat={isEmpty(value) && !isActive && !placeholder}
        error={error}
        trailingLast={
          variant === "default" ? (
            <Icon
              className={iconStyle({ isOpen: isActive })}
              icon="chevronDown"
              size="s2"
            />
          ) : undefined
        }
        placeholder={
          isEmpty(value) && placeholder && !display ? placeholder : undefined
        }
        display={display}
        variant={variant}
        proxy={
          <input
            ref={mergeRefs(proxyRef, inputRef)}
            id={id}
            className={mergeClasses(
              typography.bodyS,
              inputStyle({ proxy: true, disabled }),
              className,
            )}
            disabled={disabled}
            onKeyDown={handleProxyKeyDown}
            onChange={handleProxyChange}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            autoComplete="off"
            data-1p-ignore
          />
        }
        {...props}
      >
        {selectedLabel && (
          <div className={mergeClasses(activeOptionStyle())}>
            <Text variant={variant === "toggle" ? "bodyL" : "bodyM"} ellipsis>
              {selectedLabel}
            </Text>
          </div>
        )}
      </BaseInputField>
    </Popup>
  );
};
